import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  ChevronLeft as ChevronLeftIcon,
  Assignment as AssignmentIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import MainLayout from '../layout/MainLayout';
import DateUtils from '../utils/dateUtils';

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [completedRequests, setCompletedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markdownContent, setMarkdownContent] = useState(null);
  const [markdownLoading, setMarkdownLoading] = useState(false);
  const [availablePlots, setAvailablePlots] = useState([]);
  const [reportMetadata, setReportMetadata] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const navigate = useNavigate();
  const params = useParams();
  const requestId = params.id;

  // Laden aller abgeschlossenen Anfragen
  useEffect(() => {
    const fetchCompletedRequests = async () => {
      setLoading(true);
      try {
        const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
        const response = await axios.get(`${baseUrl}/api/requests`);
        
        // Nur abgeschlossene Anfragen filtern
        const completed = response.data.filter(req => 
          req.status === 'completed' || req.status === 'delivered'
        );
        
        console.log(`${completed.length} abgeschlossene Anfragen gefunden`);
        setCompletedRequests(completed);
        
        // Wenn eine ID in der URL ist, diese Anfrage auswählen
        if (requestId) {
          const selected = completed.find(req => req.id === requestId);
          if (selected) {
            setSelectedRequest(selected);
            // Verwende die reportFolder-Eigenschaft, falls vorhanden
            loadReportData(selected.id, selected.reportFolder);
          }
        }
      } catch (error) {
        console.error('Fehler beim Laden der abgeschlossenen Anfragen:', error);
        setError('Die Daten konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedRequests();
  }, [requestId]);

  // Laden des Reports für eine bestimmte Anfrage
  const loadReportData = async (id, reportFolder) => {
    if (!id) return;
    
    // Markdown-Inhalt laden
    setMarkdownLoading(true);
    setError(null); // Fehler zurücksetzen bei jedem neuen Ladeversuch
    try {
      const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
      console.log(`Lade Markdown-Report für ID ${id} von ${baseUrl}/api/requests/${id}/report-markdown`);
      
      // Verwende die ID als String für die API-Anfrage
      const requestId = id.toString();
      
      // Wenn reportFolder vorhanden ist, füge ihn als Query-Parameter hinzu
      const url = reportFolder 
        ? `${baseUrl}/api/requests/${requestId}/report-markdown?reportFolder=${reportFolder}`
        : `${baseUrl}/api/requests/${requestId}/report-markdown`;
      
      console.log(`API-Anfrage URL: ${url}`);
      
      const response = await axios.get(url, {
        timeout: 8000
      });
      
      console.log('Markdown-Inhalt geladen:', response.data);
      
      if (response.data && response.data.content) {
        setMarkdownContent(response.data.content);
        setAvailablePlots(response.data.availablePlots || []);
        console.log(`${response.data.content.length} Markdown-Abschnitte und ${response.data.availablePlots ? response.data.availablePlots.length : 0} Plots geladen`);
      } else {
        console.error('Unerwartetes Antwortformat:', response.data);
        // Fehler nicht global setzen, sondern nur im Log anzeigen
        console.warn(`Der Markdown-Inhalt konnte nicht geladen werden. Unerwartetes Antwortformat für ID ${id}.`);
        setMarkdownContent(null);
      }
    } catch (error) {
      console.error('Fehler beim Laden des Markdown-Inhalts:', error);
      if (error.response) {
        console.error('Fehlerantwort vom Server:', error.response.data);
        // Fehler nicht global setzen, nur im Log anzeigen
        console.warn(`Fehler beim Laden des Markdown-Inhalts für ID ${id}: ${error.response.data.message || error.message}`);
      } else if (error.request) {
        console.error('Keine Antwort vom Server erhalten');
        // Nur bei Netzwerkfehlern globalen Fehler setzen
        setError('Keine Antwort vom Server erhalten. Bitte überprüfen Sie Ihre Internetverbindung.');
      } else {
        console.warn(`Fehler beim Laden des Markdown-Inhalts für ID ${id}: ${error.message}`);
      }
      setMarkdownContent(null);
    } finally {
      setMarkdownLoading(false);
    }
    
    // Report-Metadaten laden
    setReportLoading(true);
    try {
      const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
      // Füge reportFolder als Query-Parameter hinzu, falls vorhanden
      const url = reportFolder 
        ? `${baseUrl}/api/requests/${id}/report?metadata=true&reportFolder=${reportFolder}`
        : `${baseUrl}/api/requests/${id}/report?metadata=true`;
      
      console.log(`Lade Report-Metadaten von: ${url}`);
      
      const response = await axios.get(url, {
        timeout: 5000
      });
      
      console.log('Report Metadaten geladen:', response.data);
      setReportMetadata(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Report-Metadaten:', error);
      // Fehler nicht global setzen, nur im Log anzeigen
      console.warn(`Fehler beim Laden der Report-Metadaten für ID ${id}: ${error.message}`);
      setReportMetadata(null);
    } finally {
      setReportLoading(false);
    }
  };

  const handleSelectRequest = (request) => {
    setSelectedRequest(request);
    navigate(`/reports/${request.id}`);
    loadReportData(request.id, request.reportFolder);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleDownloadReport = () => {
    if (!selectedRequest) return;
    
    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
    // Füge reportFolder als Query-Parameter hinzu, falls vorhanden
    const reportUrl = selectedRequest.reportFolder 
      ? `${baseUrl}/api/requests/${selectedRequest.id}/report?reportFolder=${selectedRequest.reportFolder}`
      : `${baseUrl}/api/requests/${selectedRequest.id}/report`;
    
    console.log(`Öffne Report-URL: ${reportUrl}`);
    
    // Öffne den Report in einem neuen Tab
    window.open(reportUrl, '_blank');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'delivered':
        return <CheckCircleIcon color="success" />;
      default:
        return <AssignmentIcon color="primary" />;
    }
  };

  return (
    <MainLayout>
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Berichte und Ergebnisse
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Hier finden Sie alle abgeschlossenen Studien mit den zugehörigen Berichten und Ergebnissen.
          </Typography>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3} direction="column">
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ borderRadius: 2 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="h2">
                  Abgeschlossene Studien
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    placeholder="Suchen..."
                    size="small"
                    variant="outlined"
                    sx={{ width: 200, mr: 2 }}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                          <SearchIcon fontSize="small" />
                        </Box>
                      ),
                    }}
                  />
                </Box>
              </Box>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : completedRequests.length > 0 ? (
                <TableContainer sx={{ maxHeight: '400px' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell width="50px"></TableCell>
                        <TableCell>Studientitel</TableCell>
                        <TableCell>Einrichtung</TableCell>
                        <TableCell>Datum</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {completedRequests
                        .filter(request => 
                          !searchTerm || 
                          (request.name || request.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (request.hospital || '').toLowerCase().includes(searchTerm.toLowerCase())
                        )
                        .slice(0, 5)
                        .map((request) => (
                          <TableRow 
                            key={request.id}
                            hover
                            selected={selectedRequest && selectedRequest.id === request.id}
                            onClick={() => handleSelectRequest(request)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                              '&.Mui-selected': { 
                                bgcolor: 'rgba(255, 122, 0, 0.08)',
                                '&:hover': { bgcolor: 'rgba(255, 122, 0, 0.12)' },
                              }
                            }}
                          >
                            <TableCell>
                              {getStatusIcon(request.status)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {request.name || request.title}
                              </Typography>
                            </TableCell>
                            <TableCell>{request.hospital || 'Unbekannte Einrichtung'}</TableCell>
                            <TableCell>{DateUtils.formatDate(request.completionDate || request.updatedAt || request.date)}</TableCell>
                            <TableCell>
                              <Chip 
                                size="small" 
                                label={request.status === 'completed' ? 'Abgeschlossen' : 'Übermittelt'} 
                                color="success" 
                                variant="outlined"
                                sx={{ height: 24, fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Bericht anzeigen">
                                <IconButton 
                                  size="small" 
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectRequest(request);
                                  }}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Keine abgeschlossenen Studien gefunden.
                  </Typography>
                </Box>
              )}
              {completedRequests.length > 0 && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Klicken Sie auf eine Zeile, um den zugehörigen Bericht anzuzeigen
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12}>
            {selectedRequest ? (
              <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" component="h2">
                    {selectedRequest.name || selectedRequest.title}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<ChevronLeftIcon />}
                    onClick={() => {
                      setSelectedRequest(null);
                      navigate('/reports');
                    }}
                  >
                    Zurück zur Übersicht
                  </Button>
                </Box>

                <Box sx={{ width: '100%', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Tabs 
                    value={activeTab} 
                    onChange={handleTabChange} 
                    variant="fullWidth"
                    indicatorColor="primary"
                    textColor="primary"
                  >
                    <Tab label="Studienbericht" />
                    <Tab label="Dateien & Downloads" />
                  </Tabs>
                </Box>

                {activeTab === 0 ? (
                  <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                        Studienbericht für: {selectedRequest.name || selectedRequest.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {selectedRequest.description || 'Keine Beschreibung verfügbar.'}
                      </Typography>
                      
                      {reportMetadata && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                          <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadReport}
                          >
                            PDF Report herunterladen
                          </Button>
                        </Box>
                      )}
                    </Box>

                    {markdownLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : markdownContent ? (
                      // Markdown-Inhalt anzeigen
                      <>
                        {markdownContent.map((section, index) => (
                          <Card 
                            elevation={section.type === 'heading1' ? 2 : 1} 
                            sx={{ 
                              mb: 3,
                              bgcolor: section.type === 'heading1' ? '#f8f9fa' : 
                                      section.type === 'heading2' ? '#ffffff' : '#fafafa',
                              borderLeft: section.type === 'heading1' ? '4px solid #1976d2' : 
                                         section.type === 'heading2' ? '3px solid #42a5f5' : 
                                         '2px solid #90caf9'
                            }}
                            key={index}
                          >
                            <CardHeader 
                              title={
                                <Typography 
                                  variant={section.type === 'heading1' ? 'h5' : 
                                          section.type === 'heading2' ? 'h6' : 'subtitle1'}
                                  sx={{ 
                                    fontWeight: section.type === 'heading1' ? 'bold' : 
                                              section.type === 'heading2' ? 'medium' : 'normal' 
                                  }}
                                >
                                  {section.title}
                                </Typography>
                              }
                              // Keine Anzeige der Abschnittsarten mehr
                            />
                            <CardContent>
                              {section.content.length > 0 && (
                                <Box sx={{ 
                                  mb: 2, 
                                  pl: section.type === 'heading2' ? 2 : 
                                      section.type === 'heading3' ? 3 : 0,
                                  borderLeft: section.type === 'heading3' ? '1px solid #e0e0e0' : 'none'
                                }}>
                                  <div className="markdown-content" 
                                    dangerouslySetInnerHTML={{ 
                                      __html: section.content.join('\n')
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                        .replace(/\*(.*?)\*\*/g, '<em>$1</em>')
                                        .replace(/^#### (.*?)$/gm, '<h4 style="margin-bottom: 0.2em; margin-top: 0.4em; font-size: 1.2em;"><strong>$1</strong></h4>')
                                        .replace(/^##### (.*?)$/gm, '<h5 style="margin-bottom: 0.2em; margin-top: 0.4em; font-size: 1.1em;"><strong>$1</strong></h5>')
                                        .replace(/\n/g, '<br />')
                                        .replace(/^- (.*)/gm, '• $1')
                                        .replace(/^(\d+)\. (.*)/gm, '$1. $2')
                                    }} 
                                  />
                                </Box>
                              )}
                              
                              {section.plots.length > 0 && (
                                <Grid container spacing={2}>
                                  {section.plots.map((plot, plotIndex) => {
                                    // Finde den entsprechenden Plot in availablePlots
                                    const matchingPlot = availablePlots.find(p => 
                                      p.filename.includes(plot.src.replace(/\.\.\//g, '').replace(/\.\//g, ''))
                                    );
                                    
                                    return (
                                      <Grid item xs={12} key={plotIndex}>
                                        <Box 
                                          sx={{ 
                                            border: '1px solid #eee', 
                                            borderRadius: 1, 
                                            p: 1, 
                                            height: 'auto',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            mb: 2
                                          }}
                                        >
                                          {plot.apiUrl ? (
                                            <>
                                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                {plot.altText}
                                              </Typography>
                                              {(() => {
                                                // Erstelle HTML-Version des Pfades für interaktive Plots
                                                const htmlApiUrl = plot.apiUrl.replace('.svg', '.html');
                                                const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:3001' : '';
                                                
                                                // Versuche zuerst die HTML-Version zu laden
                                                return (
                                                  <Box 
                                                    component="iframe"
                                                    src={`${baseUrl}${htmlApiUrl}`}
                                                    title={plot.altText}
                                                    sx={{ 
                                                      width: '100%', 
                                                      height: '600px', // Feste Standardhöhe
                                                      border: 'none',
                                                      margin: '0 auto',
                                                      display: 'block',
                                                      textAlign: 'center'
                                                    }}
                                                    onError={(e) => {
                                                      console.log("HTML-Version nicht verfügbar, versuche SVG:", htmlApiUrl);
                                                      // Wenn HTML-Version nicht verfügbar, zeige SVG
                                                      if (plot.filename && plot.filename.endsWith('.svg')) {
                                                        // SVG direkt anzeigen
                                                        const imgElement = document.createElement('img');
                                                        imgElement.src = `${baseUrl}${plot.apiUrl}`;
                                                        imgElement.alt = plot.altText;
                                                        imgElement.style.maxWidth = '100%';
                                                        imgElement.style.maxHeight = '700px';
                                                        imgElement.style.objectFit = 'contain';
                                                        
                                                        // Ersetze iframe mit img
                                                        e.target.parentNode.replaceChild(imgElement, e.target);
                                                        
                                                        // Fehlerbehandlung für das Bild
                                                        imgElement.onerror = () => {
                                                          console.error("Fehler beim Laden des SVG-Bildes:", plot.apiUrl);
                                                          imgElement.src = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+";
                                                          imgElement.style.padding = "50px";
                                                          imgElement.style.opacity = "0.5";
                                                        };
                                                      } else {
                                                        // Fallback für nicht-SVG-Dateien
                                                        e.target.style.display = 'none';
                                                        const errorMsg = document.createElement('div');
                                                        errorMsg.textContent = "Diagramm konnte nicht geladen werden";
                                                        errorMsg.style.color = '#666';
                                                        errorMsg.style.padding = '20px';
                                                        errorMsg.style.textAlign = 'center';
                                                        e.target.parentNode.appendChild(errorMsg);
                                                      }
                                                    }}
                                                  />
                                                );
                                              })()}
                                            </>
                                          ) : matchingPlot ? (
                                            <>
                                              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                                                {plot.altText}
                                              </Typography>
                                              {matchingPlot.filename.endsWith('.svg') ? (
                                                <Box 
                                                  component="img" 
                                                  src={`${window.location.hostname === 'localhost' ? 'http://localhost:3001' : ''}${matchingPlot.url}`}
                                                  alt={plot.altText}
                                                  sx={{ 
                                                    maxWidth: '100%', 
                                                    maxHeight: 700,
                                                    objectFit: 'contain',
                                                    margin: '0 auto',
                                                    display: 'block'
                                                  }}
                                                />
                                              ) : (
                                                <Box 
                                                  component="iframe"
                                                  src={`${window.location.hostname === 'localhost' ? 'http://localhost:3001' : ''}${matchingPlot.url}`}
                                                  title={plot.altText}
                                                  sx={{ 
                                                    width: '100%', 
                                                    height: '600px', // Feste Standardhöhe
                                                    border: 'none',
                                                    margin: '0 auto',
                                                    display: 'block'
                                                  }}
                                                />
                                              )}
                                            </>
                                          ) : (
                                            <Typography variant="body2" color="text.secondary">
                                              Plot nicht verfügbar: {plot.originalSrc || plot.src}
                                            </Typography>
                                          )}
                                        </Box>
                                      </Grid>
                                    );
                                  })}
                                </Grid>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </>
                    ) : (
                      <Box sx={{ p: 3 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                          Für diese Studie ist kein Markdown-Bericht verfügbar. Sie können den PDF-Bericht herunterladen, falls verfügbar.
                        </Alert>
                        
                        {reportMetadata && (
                          <Card elevation={2} sx={{ mt: 4, p: 3, textAlign: 'center' }}>
                            <PdfIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                              PDF-Report verfügbar
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              Größe: {reportMetadata.size} • Erstellt am: {reportMetadata.creationDate}
                            </Typography>
                            <Button 
                              variant="contained" 
                              color="primary" 
                              startIcon={<DownloadIcon />}
                              onClick={handleDownloadReport}
                              sx={{ mt: 2 }}
                            >
                              PDF Report herunterladen
                            </Button>
                          </Card>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Verfügbare Dateien
                    </Typography>
                    
                    {reportLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Datei</TableCell>
                              <TableCell>Beschreibung</TableCell>
                              <TableCell>Größe</TableCell>
                              <TableCell>Datum</TableCell>
                              <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* Hauptbericht (PDF) */}
                            {reportMetadata && (
                              <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PdfIcon color="error" />
                                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
                                      Hauptbericht (PDF)
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>Finaler Report mit allen Studienergebnissen und Analysen</TableCell>
                                <TableCell>{reportMetadata.size}</TableCell>
                                <TableCell>{reportMetadata.creationDate}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Herunterladen">
                                    <IconButton size="small" onClick={handleDownloadReport} color="primary">
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Weitere Dateien aus den Ergebnissen */}
                            {selectedRequest.results && selectedRequest.results.files && 
                             selectedRequest.results.files.map((file, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    {file.type === 'pdf' ? (
                                      <PdfIcon color="error" />
                                    ) : (
                                      <FileIcon color="primary" />
                                    )}
                                    <Typography variant="body2" sx={{ ml: 1 }}>
                                      {file.name}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>{file.description || 'Keine Beschreibung verfügbar'}</TableCell>
                                <TableCell>{file.size || 'Unbekannt'}</TableCell>
                                <TableCell>{file.date || 'Unbekannt'}</TableCell>
                                <TableCell align="right">
                                  <Tooltip title="Herunterladen">
                                    <IconButton size="small" color="primary">
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                            
                            {/* Wenn keine Dateien verfügbar sind */}
                            {(!reportMetadata && (!selectedRequest.results || !selectedRequest.results.files || selectedRequest.results.files.length === 0)) && (
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  <Typography variant="body2" color="text.secondary">
                                    Keine Dateien verfügbar
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                    
                    {selectedRequest.results && selectedRequest.results.keyFindings && selectedRequest.results.keyFindings.length > 0 && (
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                          Wichtigste Ergebnisse
                        </Typography>
                        <List>
                          {selectedRequest.results.keyFindings.map((finding, index) => (
                            <ListItem key={index}>
                              <ListItemIcon>
                                <CheckCircleIcon color="success" />
                              </ListItemIcon>
                              <ListItemText primary={finding} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            ) : (
              <Paper elevation={2} sx={{ p: 4, borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <AssignmentIcon sx={{ fontSize: 60, color: 'primary.main', opacity: 0.7, mb: 2 }} />
                <Typography variant="h6" align="center" gutterBottom>
                  Wählen Sie eine Studie aus
                </Typography>
                <Typography variant="body1" align="center" color="text.secondary">
                  Bitte wählen Sie eine abgeschlossene Studie aus der Liste links, um den zugehörigen Bericht anzuzeigen.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </MainLayout>
  );
};

export default ReportsPage;
